import Vue from 'vue';
import { injectable } from 'inversify'
import { ApiService } from './ApiService'
import { IModel } from '@/model/model'
import TYPES from '@/config/Types'
import { DependencyContainer } from '@/config/DependencyContainer'
import { Meta } from '@/model/meta'
import { IComment } from '@/model/comment'
import { IUser } from '@/model/user'
import { IReference } from '@/model/reference'
import { WebAuthContainer } from '@/service/WebAuthContainer';
import { IStatement } from '@/model/statement';

export interface MetaService<ModelType extends IModel> {
  getById(id: string): Promise<ModelType>
  getNumLikes(model: ModelType): Promise<Meta<number>>
  getIsLiked(model: ModelType): Promise<Meta<boolean>>
  like(model: ModelType): Promise<ModelType>
  unlike(model: ModelType): Promise<ModelType>
  getNumJybes(model: ModelType): Promise<Meta<number>>
  getIsJybed(model: ModelType): Promise<Meta<boolean>>
  getIsJybedForUser(model: ModelType, user: IUser): Promise<Meta<boolean>>
  jybe(model: ModelType): Promise<ModelType>
  unjybe(model: ModelType): Promise<ModelType>
  getIsConsidered(model: ModelType): Promise<Meta<boolean>>
  consider(model: ModelType): Promise<ModelType>
  unconsider(model: ModelType): Promise<ModelType>
  getIsIgnored(model: ModelType): Promise<Meta<boolean>>
  ignore(model: ModelType): Promise<ModelType>
  unignore(model: ModelType): Promise<ModelType>
  getCreator(model: ModelType): Promise<IUser>
  getComments(model: ModelType): Promise<IComment[]>
  addComment(model: ModelType, comment: IComment, statements?: IStatement[]|null): Promise<IComment>
  getStatements(model: ModelType): Promise<IStatement[]>
  getTags(model: ModelType): Promise<Meta<string[]>>
  addTag(model: ModelType, tag: string): Promise<ModelType>
  removeTag(model: ModelType, tag: string): Promise<ModelType>
  getReferences(model: ModelType): Promise<IReference[]>
  addReference(model: ModelType, reference: IReference): Promise<ModelType>
  removeReference(model: ModelType, reference: IReference): Promise<ModelType>
  getNumFollowers(model: ModelType): Promise<Meta<number>>
  getIsFollowed(model: ModelType): Promise<Meta<boolean>>
  follow(model: ModelType): Promise<ModelType>
  unfollow(model: ModelType): Promise<ModelType>
}

@injectable()
export class MetaServiceImpl<ModelType extends IModel> implements MetaService<ModelType> {

  protected apiService: ApiService | null | undefined

  protected serviceName: string = ''
  
  protected webAuthContainer: WebAuthContainer

  constructor () {
    this.apiService = DependencyContainer.getContainer().get<ApiService>(TYPES.ApiService)
    this.webAuthContainer = DependencyContainer.getContainer().get<WebAuthContainer>(TYPES.WebAuthContainer)
  }
  
  public getById(id: string): Promise<ModelType> {
    return this.getApiService().getSingle<ModelType>(`${ this.serviceName }/${ id }/`)
  }
  
  public getCreator(model: ModelType): Promise<IUser> {
    return this.getApiService().getSingle<IUser>(`${ this.serviceName }/${ model.id }/creator/`)
  }

  public getComments(model: ModelType): Promise<IComment[]> {
    return this.getApiService().get<IComment[]>(`${ this.serviceName }/${ model.id }/comments/`)
  }

  public addComment(model: ModelType, comment: IComment, statements: IStatement[]|null): Promise<IComment> {
    return this.getApiService().post<IComment>(`${ this.serviceName }/${ model.id }/comments/`, { comment: comment, 'statements': statements })
  }
  
  public getStatements(model: ModelType): Promise<IStatement[]> {
    return this.getApiService().get<IStatement[]>(`${ this.serviceName }/${ model.id }/statements/`)
  }

  public getTags(model: ModelType): Promise<Meta<string[]>> {
    return this.getApiService().getAny<Meta<string[]>>(`${ this.serviceName }/${ model.id }/tags/`)
  }

  public addTag(model: ModelType, tag: string): Promise<ModelType> {
    return this.getApiService().post<ModelType>(`${ this.serviceName }/${ model.id }/tags/`, { tags: [tag] })
  }

  public removeTag(model: ModelType, tag: string): Promise<ModelType> {
    return this.getApiService().deleteWithParams<ModelType>(`${ this.serviceName }/${ model.id }/tags/`, { tag: tag })
  }

  public getReferences(model: ModelType): Promise<IReference[]> {
    return this.getApiService().getAny<IReference[]>(`${ this.serviceName }/${ model.id }/references/`)
  }

  public addReference(model: ModelType, reference: IReference): Promise<ModelType> {
    return this.getApiService().post<ModelType>(`${ this.serviceName }/${ model.id }/references/`, { references: [reference] })
  }

  public removeReference(model: ModelType, reference: IReference): Promise<ModelType> {
    return this.getApiService().deleteWithParams<ModelType>(`${ this.serviceName }/${ model.id }/references/`, { reference: reference })
  }

  public getNumLikes(model: ModelType): Promise<Meta<number>> {
    return this.getApiService().getAny<Meta<number>>(`${ this.serviceName }/${ model.id }/likes/count`)
  }

  public getIsLiked(model: ModelType): Promise<Meta<boolean>> {
    return this.getApiService().getAny<Meta<boolean>>(`${ this.serviceName }/${ model.id }/likes/user`)
  }

  public like(model: ModelType): Promise<ModelType> {
    return this.getApiService().post<ModelType>(`${ this.serviceName }/${ model.id }/likes/`, { })
  }

  public unlike(model: ModelType): Promise<ModelType> {
    return this.getApiService().deleteSub<ModelType>(this.serviceName, <number>model.id, 'likes')
  }

  public getNumJybes(model: ModelType): Promise<Meta<number>> {
    return this.getApiService().getAny<Meta<number>>(`${ this.serviceName }/${ model.id }/jybes/count`)
  }

  public getIsJybed(model: ModelType): Promise<Meta<boolean>> {
    return this.getApiService().getAny<Meta<boolean>>(`${ this.serviceName }/${ model.id }/jybes/user`)
  }

  public getIsJybedForUser(model: ModelType, user: IUser): Promise<Meta<boolean>> {
    return this.getApiService().getAny<Meta<boolean>>(`${ this.serviceName }/${ model.id }/jybes/${ user.id }`)
  }

  public jybe(model: ModelType): Promise<ModelType> {
    return this.getApiService().post<ModelType>(`${ this.serviceName }/${ model.id }/jybes/`, { })
  }

  public unjybe(model: ModelType): Promise<ModelType> {
   return this.getApiService().deleteSub<ModelType>(this.serviceName, <number>model.id, 'jybes')
  }

  public getIsConsidered(model: ModelType): Promise<Meta<boolean>> {
    return this.getApiService().getAny<Meta<boolean>>(`${ this.serviceName }/${ model.id }/maybes/user`)
  }

  public consider(model: ModelType): Promise<ModelType> {
    return this.getApiService().post<ModelType>(`${ this.serviceName }/${ model.id }/maybes/`, { })
  }

  public unconsider(model: ModelType): Promise<ModelType> {
    return this.getApiService().deleteSub<ModelType>(this.serviceName, <number>model.id, 'maybes')
  }

  public getIsIgnored(model: ModelType): Promise<Meta<boolean>> {
    return this.getApiService().getAny<Meta<boolean>>(`${ this.serviceName }/${ model.id }/ignores/user`)
  }

  public ignore(model: ModelType): Promise<ModelType> {
    return this.getApiService().post<ModelType>(`${ this.serviceName }/${ model.id }/ignores/`, { })
  }

  public unignore(model: ModelType): Promise<ModelType> {
    return this.getApiService().deleteSub<ModelType>(this.serviceName, <number>model.id, 'ignores')
  }

  public getNumFollowers(model: ModelType): Promise<Meta<number>> {
    return this.getApiService().getAny<Meta<number>>(`${ this.serviceName }/${ model.id }/followers/count`)
  }

  public getIsFollowed(model: ModelType): Promise<Meta<boolean>> {
    return this.getApiService().getAny<Meta<boolean>>(`${ this.serviceName }/${ model.id }/followers/user`)
  }

  public follow(model: ModelType): Promise<ModelType> {
    return this.getApiService().post<ModelType>(`${ this.serviceName }/${ model.id }/followers/`, { })
  }

  public unfollow(model: ModelType): Promise<ModelType> {
    return this.getApiService().deleteSub<ModelType>(this.serviceName, <number>model.id, 'followers')
  }

  protected getApiService() : ApiService {
    if (this.apiService) return this.apiService;
    else throw('ApiService is null')
  }
}
